import { HttpBackend, HttpClient } from '@angular/common/http';
import { DOCUMENT, Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject, NEVER, Observable, catchError, exhaustMap, filter, map, tap, timer } from 'rxjs';

import {
    AFTER_LOGIN_REDIRECT_URL_PROVIDER,
    AfterLoginRedirectUrlProvider,
    ConfigurationService,
    Logger
} from '@mobi/rwc-utils-common-ng-jslib';

import { TOKEN_REFRESH_PERIOD } from './token-util';

const APP_SERVICE_REFRESH_TOKENS_ENDPOINT = '/.auth/refresh';
const APP_SERVICE_TOKEN_ENDPOINT = '/.auth/me';

@Injectable({
    providedIn: 'root'
})
export class AppServiceIntegrationService {
    #httpClient = new HttpClient(inject(HttpBackend)); // avoid all http interceptors
    #logger = inject(Logger).createCustomLogger('AppServiceIntegrationService');
    #ngZone = inject(NgZone);
    #redirectUrlProvider = inject<AfterLoginRedirectUrlProvider>(AFTER_LOGIN_REDIRECT_URL_PROVIDER, { optional: true });

    #location: Location = inject(DOCUMENT).defaultView?.location as Location;
    #accessTokenChangedSubject = new BehaviorSubject(inject(ConfigurationService).getSync('accessToken') as string);
    readonly accessTokenChanged$ = this.#accessTokenChangedSubject.pipe(filter(Boolean));

    init(): void {
        this.startRefreshTokensTimers();
    }

    fetchNewAccessToken(): Observable<string> {
        return this.refreshTokens().pipe(
            exhaustMap(() => this.fetchAccessToken()),
            tap(token => this.#accessTokenChangedSubject.next(token)),
            tap(() => this.#logger.debug('Fetched new access token')),
            catchError(error => {
                this.#logger.error('Error fetching access token.', { exception: error });
                this.redirectToLoginPage();
                return NEVER;
            })
        );
    }

    private fetchAccessToken(): Observable<string> {
        return this.#httpClient
            .get<AccessTokenResponse[]>(APP_SERVICE_TOKEN_ENDPOINT)
            .pipe(map(response => this.extractAccessToken(response)));
    }

    private startRefreshTokensTimers() {
        this.#ngZone.runOutsideAngular(() => {
            timer(0, TOKEN_REFRESH_PERIOD)
                .pipe(exhaustMap(() => this.fetchNewAccessToken()))
                .subscribe({
                    error: exception => {
                        this.#logger.error(`error refreshing tokens because user is not logged in anymore`, {
                            exception
                        });
                        this.redirectToLoginPage();
                    }
                });
        });
    }

    private extractAccessToken(result: AccessTokenResponse[]): string {
        if (result.length > 0) {
            if (result.length > 1) {
                this.#logger.warn('multiple identities found, using first.');
            }
            return result[0].access_token || result[0].accessToken;
        }
        throw new Error('no access token available');
    }

    private redirectToLoginPage(): void {
        const actualUrl = this.#location.href;
        const targetUrl = this.#redirectUrlProvider?.get();
        const redirectUrl = encodeURIComponent(targetUrl ?? actualUrl);
        this.#logger.info(`redirecting from page ${actualUrl} to login page with redirectUrl: ${targetUrl}`);
        this.#location.href = `/.auth/login/aad?post_login_redirect_url=${redirectUrl}`;
    }

    private refreshTokens() {
        return this.#httpClient
            .get<unknown[]>(APP_SERVICE_REFRESH_TOKENS_ENDPOINT)
            .pipe(tap(() => this.#logger.debug('Tokens refreshed on Azure App Service')));
    }
}

interface AccessTokenResponse {
    access_token: string;
    accessToken: string;
}
